import CloseIcon from "@mui/icons-material/Close";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  forwardRef,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { getMapErrorMessage } from "../../../providers/map/NeshanMapProvider";
import {
  mapService,
  MapLocation,
  MapSearchResult,
  MapServiceError,
} from "../../../services/map/mapService";
import MapSkeleton from "./MapSkeleton";

const NeshanMapView = lazy(() => import("./NeshanMapView"));

export interface LocationPickerHandle {
  confirm: () => Promise<void>;
}

export interface LocationPickerProps {
  initialLat?: number | null;
  initialLng?: number | null;
  onChange?: (location: MapLocation) => void;
  readonly?: boolean;
  height?: number | string;
  /** compact layout for Dialog — map height capped, actions expected in DialogActions */
  variant?: "default" | "dialog";
  showActions?: boolean;
  onConfirm?: (location: MapLocation) => void;
  onCancel?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  onPositionChange?: (hasPosition: boolean) => void;
  onLoadingChange?: (loading: boolean) => void;
}

const LocationPicker = forwardRef<LocationPickerHandle, LocationPickerProps>(
  function LocationPicker(
    {
      initialLat = null,
      initialLng = null,
      onChange,
      readonly = false,
      height = 400,
      variant = "default",
      showActions = false,
      onConfirm,
      onCancel,
      confirmLabel = "تایید",
      cancelLabel = "لغو",
      onPositionChange,
      onLoadingChange,
    },
    ref
  ) {
  const isDialogVariant = variant === "dialog";
  const mapHeight = isDialogVariant ? 240 : height;
  const [position, setPosition] = useState<[number, number] | null>(() => {
    if (initialLat != null && initialLng != null) {
      return [Number(initialLat), Number(initialLng)];
    }
    return null;
  });
  const [mapCenter, setMapCenter] = useState<[number, number]>(() => {
    if (initialLat != null && initialLng != null) {
      return [Number(initialLat), Number(initialLng)];
    }
    return mapService.getDefaultCenter();
  });
  const [mapZoom, setMapZoom] = useState<number>(
    initialLat != null && initialLng != null
      ? mapService.getPickerZoom()
      : mapService.getDefaultZoom()
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<MapSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [selectedAddress, setSelectedAddress] = useState("");
  const lastSearchPickRef = useRef<MapSearchResult | null>(null);
  const searchAbortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (initialLat != null && initialLng != null) {
      const nextPosition: [number, number] = [Number(initialLat), Number(initialLng)];
      setPosition(nextPosition);
      setMapCenter(nextPosition);
      setMapZoom(mapService.getPickerZoom());
    }
  }, [initialLat, initialLng]);

  const updatePosition = useCallback(
    (lat: number, lng: number, moveMap = true) => {
      const nextPosition: [number, number] = [lat, lng];
      setPosition(nextPosition);
      if (moveMap) {
        setMapCenter(nextPosition);
        setMapZoom(mapService.getPickerZoom());
      }
    },
    []
  );

  const resolveAddress = useCallback(
    async (lat: number, lng: number, options?: { silent?: boolean }) => {
      try {
        const location = await mapService.reverseGeocode(lat, lng);
        setSelectedAddress(location.formattedAddress || location.address || "");
        return location;
      } catch (error) {
        const message = getMapErrorMessage(error);
        if (
          !options?.silent &&
          error instanceof MapServiceError &&
          error.code !== "NETWORK_ERROR"
        ) {
          toast.error(message);
        }
        const fallback: MapLocation = {
          lat,
          lng,
          address: selectedAddress.trim(),
          formattedAddress: selectedAddress.trim() || undefined,
          provider: "neshan",
        };
        return fallback;
      }
    },
    [selectedAddress]
  );

  const buildLocationFromSearchPick = useCallback(
    (lat: number, lng: number, pick: MapSearchResult): MapLocation => ({
      lat,
      lng,
      address: pick.address || pick.title,
      formattedAddress: pick.address || pick.title,
      city: pick.city,
      district: pick.district,
      provider: "neshan",
    }),
    []
  );

  const runSearch = useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) {
        setSearchResults([]);
        return;
      }

      searchAbortRef.current?.abort();
      const controller = new AbortController();
      searchAbortRef.current = controller;

      setSearchLoading(true);
      try {
        const [centerLat, centerLng] = mapCenter;
        const results = await mapService.search(trimmed, {
          lat: centerLat,
          lng: centerLng,
          limit: 5,
          signal: controller.signal,
        });
        setSearchResults(results);
      } catch (error) {
        if (error instanceof MapServiceError && error.code === "TIMEOUT") {
          return;
        }
        setSearchResults([]);
        toast.error(getMapErrorMessage(error));
      } finally {
        setSearchLoading(false);
      }
    },
    [mapCenter]
  );

  useEffect(() => {
    if (readonly) {
      return;
    }

    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }

    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    debounceRef.current = window.setTimeout(() => {
      runSearch(searchQuery);
    }, mapService.getSearchDebounceMs());

    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [readonly, runSearch, searchQuery]);

  const handleSelectSearchResult = useCallback(
    (result: MapSearchResult) => {
      lastSearchPickRef.current = result;
      updatePosition(result.lat, result.lng);
      setSelectedAddress(result.address || result.title);
      setSearchResults([]);
      setSearchQuery(result.title);
    },
    [updatePosition]
  );

  const handleUseCurrentLocation = useCallback(() => {
    setGeoError(null);

    if (!navigator.geolocation) {
      setGeoError("مرورگر از موقعیت‌یاب (GPS) پشتیبانی نمی‌کند.");
      return;
    }

    if (!window.isSecureContext) {
      setGeoError(
        "برای استفاده از موقعیت فعلی، سایت باید از طریق HTTPS یا localhost باز شود."
      );
      return;
    }

    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        updatePosition(latitude, longitude);
        setGeoLoading(false);
      },
      (err) => {
        const messages: Record<number, string> = {
          1: "دسترسی به موقعیت مجاز نیست.",
          2: "موقعیت در دسترس نیست.",
          3: "زمان درخواست موقعیت به پایان رسید.",
        };
        setGeoError(messages[err.code] || err.message || "خطا در دریافت موقعیت.");
        setGeoLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, [updatePosition]);

  const handleConfirm = useCallback(async () => {
    if (!position || readonly) {
      return;
    }

    setAddressLoading(true);
    try {
      const [lat, lng] = position;
      const pick = lastSearchPickRef.current;
      let location: MapLocation;

      if (
        pick &&
        Math.abs(pick.lat - lat) < 0.000001 &&
        Math.abs(pick.lng - lng) < 0.000001
      ) {
        location = buildLocationFromSearchPick(lat, lng, pick);
      } else if (selectedAddress.trim()) {
        location = {
          lat,
          lng,
          address: selectedAddress.trim(),
          formattedAddress: selectedAddress.trim(),
          provider: "neshan",
        };
      } else {
        location = await resolveAddress(lat, lng, { silent: true });
      }

      onChange?.(location);
      onConfirm?.(location);
    } finally {
      setAddressLoading(false);
    }
  }, [
    buildLocationFromSearchPick,
    onChange,
    onConfirm,
    position,
    readonly,
    resolveAddress,
    selectedAddress,
  ]);

  useImperativeHandle(ref, () => ({ confirm: handleConfirm }), [handleConfirm]);

  useEffect(() => {
    onPositionChange?.(!!position);
  }, [position, onPositionChange]);

  useEffect(() => {
    onLoadingChange?.(addressLoading);
  }, [addressLoading, onLoadingChange]);

  const coordinatesLabel = useMemo(() => {
    if (!position) {
      return null;
    }
    return `Latitude: ${position[0].toFixed(6)} | Longitude: ${position[1].toFixed(6)}`;
  }, [position]);

  const showInlineActions = showActions && !readonly && !isDialogVariant;

  return (
    <Box
      sx={
        isDialogVariant
          ? {
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              flex: 1,
              overflow: "hidden",
            }
          : undefined
      }
    >
      {!readonly && (
        <Box sx={{ mb: isDialogVariant ? 1.5 : 2, flexShrink: 0 }}>
          <TextField
            fullWidth
            size={isDialogVariant ? "small" : "medium"}
            placeholder="جستجوی آدرس (مثال: تهران، میدان آزادی)"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                runSearch(searchQuery);
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <CloseIcon
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      setSearchQuery("");
                      setSearchResults([]);
                    }}
                  />
                </InputAdornment>
              ) : undefined,
            }}
          />

          <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size={isDialogVariant ? "small" : "medium"}
              onClick={() => runSearch(searchQuery)}
              disabled={searchLoading || !searchQuery.trim()}
              startIcon={searchLoading ? <CircularProgress size={16} /> : undefined}
            >
              جستجو
            </Button>
            <Button
              variant="outlined"
              size={isDialogVariant ? "small" : "medium"}
              onClick={handleUseCurrentLocation}
              disabled={geoLoading}
              startIcon={
                geoLoading ? <CircularProgress size={16} /> : <MyLocationIcon />
              }
            >
              موقعیت فعلی من
            </Button>
          </Box>

          {geoError && (
            <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setGeoError(null)}>
              {geoError}
            </Alert>
          )}
        </Box>
      )}

      {!readonly && searchResults.length > 0 && (
        <Paper
          sx={{
            mb: isDialogVariant ? 1 : 2,
            maxHeight: isDialogVariant ? 120 : 180,
            overflow: "auto",
            flexShrink: 0,
          }}
        >
          <List dense>
            {searchResults.map((result) => (
              <ListItem key={result.id} disablePadding>
                <ListItemButton onClick={() => handleSelectSearchResult(result)}>
                  <ListItemText
                    primary={result.title}
                    secondary={result.address}
                    primaryTypographyProps={{ variant: "body2" }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {!readonly && searchQuery.trim() && !searchLoading && searchResults.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          نتیجه‌ای یافت نشد.
        </Typography>
      )}

      <Box
        sx={{
          flexShrink: 0,
          width: "100%",
          ...(isDialogVariant && { height: mapHeight, minHeight: 200 }),
        }}
      >
        <Suspense fallback={<MapSkeleton height={mapHeight} />}>
          <NeshanMapView
            center={mapCenter}
            zoom={mapZoom}
            height={mapHeight}
            readOnly={readonly}
            markerPosition={position}
            draggableMarker={!readonly}
            onMapClick={(lat, lng) => {
              lastSearchPickRef.current = null;
              setSelectedAddress("");
              updatePosition(lat, lng, false);
            }}
            onMarkerDragEnd={(lat, lng) => {
              lastSearchPickRef.current = null;
              setSelectedAddress("");
              updatePosition(lat, lng, false);
            }}
          />
        </Suspense>
      </Box>

      {position && (
        <Box
          sx={{
            mt: isDialogVariant ? 1 : 2,
            textAlign: "center",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: isDialogVariant ? "0.8125rem" : undefined,
            }}
          >
            مختصات انتخاب شده
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: isDialogVariant ? "0.75rem" : undefined }}
          >
            {coordinatesLabel}
          </Typography>
          {selectedAddress && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mt: 0.5,
                fontSize: isDialogVariant ? "0.6875rem" : undefined,
              }}
            >
              {selectedAddress}
            </Typography>
          )}
        </Box>
      )}

      {showInlineActions && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
          {onCancel && (
            <Button onClick={onCancel} color="inherit">
              {cancelLabel}
            </Button>
          )}
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={!position || addressLoading}
            startIcon={addressLoading ? <CircularProgress size={16} /> : undefined}
          >
            {addressLoading ? "در حال دریافت آدرس..." : confirmLabel}
          </Button>
        </Box>
      )}
    </Box>
  );
});

LocationPicker.displayName = "LocationPicker";

export default LocationPicker;
